import { useEffect, useState } from "react";
import { businessesApi, vatApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { Save, Loader2, Sliders, Wallet } from "lucide-react";

// Per-business defaults page. Exposes:
//   • Default tax code — pre-fills new documents so line items cascade.
//   • Auto-extract line items — when off, OCR still runs but line items are
//     left empty; the user pulls them on-demand from the doc via the
//     "Extract line items" button. Some vendors want header-only bills
//     (single-line utility bills, rentals, etc.) — this toggle keeps their
//     drafts clean.
//   • Cash Up integration (iter98) — when a doc is paid with the configured
//     petty-cash account, DocHub posts a `cash_out` to the external Cash Up
//     app for the specified entity so cash-drawer reconciliation is real-time.
export default function BusinessDefaults() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const load = () => businessesApi.list().then(setBusinesses);
  useEffect(() => { load(); }, []);
  const visible = isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="biz-defaults-title">Business defaults</h1>
        <p className="text-espresso-ink/60 mt-2 max-w-2xl">
          Configure per-business defaults for new documents. Existing documents
          are untouched — this only affects docs going forward.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((b) => (
          <BusinessCard key={b.id} biz={b} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

function BusinessCard({ biz, onSaved }) {
  const [vats, setVats] = useState([]);
  const [taxId, setTaxId] = useState(biz.default_tax_code_id || "");
  const [autoExtract, setAutoExtract] = useState(biz.auto_extract_line_items !== false);
  const [cashupEnabled, setCashupEnabled] = useState(!!biz.cashup_enabled);
  const [cashupEntityId, setCashupEntityId] = useState(biz.cashup_entity_id || "");
  const [cashupPettyCode, setCashupPettyCode] = useState(biz.cashup_petty_cash_account_code || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { vatApi.list(biz.id).then(setVats).catch(() => setVats([])); }, [biz.id]);

  const initialAuto = biz.auto_extract_line_items !== false;
  const dirty =
    (taxId || "") !== (biz.default_tax_code_id || "") ||
    autoExtract !== initialAuto ||
    cashupEnabled !== !!biz.cashup_enabled ||
    (cashupEntityId || "") !== (biz.cashup_entity_id || "") ||
    (cashupPettyCode || "") !== (biz.cashup_petty_cash_account_code || "");

  const save = async () => {
    setSaving(true);
    try {
      await businessesApi.updateSettings(biz.id, {
        default_tax_code_id: taxId || null,
        auto_extract_line_items: autoExtract,
        cashup_enabled: cashupEnabled,
        cashup_entity_id: cashupEntityId || null,
        cashup_petty_cash_account_code: cashupPettyCode || null,
      });
      toast.success(`Defaults saved for ${biz.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-canvas-border p-5 shadow-soft" data-testid={`biz-defaults-${biz.code}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-gold-soft flex items-center justify-center text-gold rounded-md"><Sliders className="w-4 h-4" /></div>
        <div className="font-display font-bold text-espresso">{biz.name}</div>
      </div>

      <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Default tax code</label>
      <select
        data-testid={`biz-default-tax-${biz.code}`}
        value={taxId}
        onChange={(e) => setTaxId(e.target.value)}
        className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border rounded-lg text-sm brand-focus"
      >
        <option value="">— none —</option>
        {vats.map((v) => (
          <option key={v.id} value={v.id}>{v.name} ({v.rate}%)</option>
        ))}
      </select>
      <div className="text-xs text-espresso-ink/60 mt-1">
        {vats.length === 0 ? "No VAT codes yet — sync from Accounting." : `${vats.length} VAT code${vats.length === 1 ? "" : "s"} available`}
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`biz-auto-extract-${biz.code}`}
          checked={autoExtract}
          onChange={(e) => setAutoExtract(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-espresso cursor-pointer"
        />
        <div>
          <div className="text-sm font-semibold text-espresso">Auto-extract line items on upload</div>
          <div className="text-xs text-espresso-ink/60 mt-0.5">
            When off, new documents come in with just header totals. Pull line
            items on demand from the doc using the "Extract line items" button.
          </div>
        </div>
      </label>

      <div className="mt-5 pt-5 border-t border-canvas-border">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-4 h-4 text-espresso-ink/60" />
          <div className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Cash Up integration</div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`biz-cashup-enabled-${biz.code}`}
            checked={cashupEnabled}
            onChange={(e) => setCashupEnabled(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-espresso cursor-pointer"
          />
          <div className="flex-1">
            <div className="text-sm font-semibold text-espresso">Post Petty Cash spends to Cash Up</div>
            <div className="text-xs text-espresso-ink/60 mt-0.5">
              After publish, if the doc's payment method matches the account
              code below, DocHub posts a <code className="text-[10px]">cash_out</code> to Cash Up
              with the receipt attached.
            </div>
          </div>
        </label>
        {cashupEnabled && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-espresso-ink/60 font-semibold">Cash Up entity ID</label>
              <input
                type="text"
                data-testid={`biz-cashup-entity-${biz.code}`}
                value={cashupEntityId}
                onChange={(e) => setCashupEntityId(e.target.value)}
                placeholder="e.g. 28d538da-0cac-4798-9d06-04adf3a556fd"
                className="w-full mt-1 px-3 py-2 bg-white border border-canvas-border rounded text-xs font-mono brand-focus"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-espresso-ink/60 font-semibold">Petty Cash account code</label>
              <input
                type="text"
                data-testid={`biz-cashup-petty-${biz.code}`}
                value={cashupPettyCode}
                onChange={(e) => setCashupPettyCode(e.target.value)}
                placeholder="e.g. 1000"
                className="w-full mt-1 px-3 py-2 bg-white border border-canvas-border rounded text-xs font-mono brand-focus"
              />
              <div className="text-[11px] text-espresso-ink/50 mt-1">Must match the payment method value on a doc for the trigger to fire.</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end mt-4">
        <button
          data-testid={`biz-default-save-${biz.code}`}
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-espresso text-canvas rounded-md text-xs font-semibold hover:bg-espresso-light disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}
